import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons/static";
import type { ComponentProps } from "react";
import { View, type ViewStyle } from "react-native";
import { Appbar, List, Menu } from "react-native-paper";

type IconName = ComponentProps<typeof MaterialCommunityIcons>["name"];
type IconProps = Omit<ComponentProps<typeof MaterialCommunityIcons>, "name">;

const renderOverflowIcon = (props: IconProps) => (
	<MaterialCommunityIcons
		name="dots-vertical"
		{...props}
		importantForAccessibility="no"
		aria-hidden
	/>
);

const DecorativeIcon = ({
	name,
	color,
	style,
}: {
	name: IconName;
	color: string;
	style: ViewStyle;
}) => (
	<View
		style={style}
		importantForAccessibility="no-hide-descendants"
		aria-hidden
	>
		<MaterialCommunityIcons
			name={name}
			color={color}
			size={24}
			importantForAccessibility="no"
			aria-hidden
		/>
	</View>
);

export type PreferencesMenuProps = {
	visible: boolean;
	accessibilityLabel: string;
	iconColor?: string;
	themeTitle: string;
	themeSubtitle: string;
	languageTitle?: string;
	languageSubtitle?: string;
	onOpen: () => void;
	onDismiss: () => void;
	onPressTheme: () => void;
	onPressLanguage?: () => void;
};

export const PreferencesMenu = ({
	visible,
	accessibilityLabel,
	iconColor,
	themeTitle,
	themeSubtitle,
	languageTitle,
	languageSubtitle,
	onOpen,
	onDismiss,
	onPressTheme,
	onPressLanguage,
}: PreferencesMenuProps) => {
	const showLanguage = Boolean(
		languageTitle && languageSubtitle && onPressLanguage,
	);

	return (
		<Menu
			visible={visible}
			onDismiss={onDismiss}
			anchorPosition="bottom"
			anchor={
				<Appbar.Action
					icon={renderOverflowIcon}
					color={iconColor}
					accessibilityLabel={accessibilityLabel}
					onPress={onOpen}
				/>
			}
		>
			<List.Item
				accessibilityRole="button"
				accessibilityLabel={`${themeTitle}, ${themeSubtitle}`}
				left={(props) => <DecorativeIcon {...props} name="theme-light-dark" />}
				title={themeTitle}
				description={themeSubtitle}
				onPress={onPressTheme}
			/>
			{showLanguage ? (
				<List.Item
					accessibilityRole="button"
					accessibilityLabel={`${languageTitle}, ${languageSubtitle}`}
					left={(props) => <DecorativeIcon {...props} name="translate" />}
					title={languageTitle}
					description={languageSubtitle}
					onPress={onPressLanguage}
				/>
			) : null}
		</Menu>
	);
};
